import type { ReactNode } from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { Tooltip } from '@odo/shared-ui';
import type { WorkQueueCase } from '../../services/workQueue';
import { useStatusCatalog } from '../../services/statusCatalog';
import { useAssignableMembers } from '../../hooks/useRoles';
import { useUser } from '../../providers/UserProvider';
import { practiceDoctorLabel } from './util';
import { initials } from './caseInitials';
import { PRIORITY_LEVELS } from './priority/priority';
import { PriorityGlyph } from './priority/PriorityGlyphs';
import { PRIORITY_LABEL_TONES } from './priority/priorityTones';
import { TeamPill } from '@odo/shared-ui/components/TeamPill';
import { AssigneeAvatar } from '../conversations/AssigneeAvatar';
import {
    compactName,
    getAvatarColor,
    nameFromEmail
} from '../conversations/assigneeSelectorHelpers';

export interface NextCaseLink {
    workCase: WorkQueueCase;
    remaining: number;
    // How the walked list differs from the default view; both empty when it doesn't.
    filterChips: readonly string[];
    sortChip: string | null;
    onOpen: () => void;
}

const SECTION_LABEL = 'text-[10px] font-bold tracking-[0.08em] uppercase text-gray-400';
const FACT_VALUE = 'text-[12.5px] font-semibold text-gray-800';
const BAND_CHIP =
    'rounded-md border border-gray-200 bg-white px-1.5 py-0.5 text-[11px] font-medium text-gray-600';

// The case's own-team slot first, as CompletionCardAssignees seeds it; display-only, so
// AssigneeName (which carries its own tooltip) is left out.
function CaseAssignee({ workCase: c }: { workCase: WorkQueueCase }) {
    const email = c.teamAssignees?.[c.team] ?? c.assignee;
    const { user } = useUser();
    const { members } = useAssignableMembers(undefined, Boolean(email));
    if (!email) return <span className="text-[12.5px] text-gray-400">Unassigned</span>;
    const fullname = members.find((m) => m.email === email)?.fullname ?? nameFromEmail(email);
    return (
        <span className="inline-flex items-center gap-1.5 min-w-0">
            <AssigneeAvatar name={fullname} color={getAvatarColor(fullname)} size="sm" />
            <span className={`truncate ${FACT_VALUE}`}>
                {email === user?.email ? 'Me' : compactName(fullname)}
            </span>
        </span>
    );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="min-w-0 px-3 py-3 first:pl-4 last:pr-4">
            <div className={`${SECTION_LABEL} mb-1.5`}>{label}</div>
            <div className="flex items-center min-h-[20px]">{children}</div>
        </div>
    );
}

function CaseFacts({ workCase: c }: { workCase: WorkQueueCase }) {
    const catalog = useStatusCatalog();
    return (
        <div className="grid grid-cols-[1.6fr_auto_1fr] divide-x divide-gray-100 border-t border-gray-100">
            <Fact label="Status">
                <span className={FACT_VALUE}>{catalog.display(c.statusId)}</span>
            </Fact>
            <Fact label="Team">
                <TeamPill team={c.team} size="xs" />
            </Fact>
            <Fact label="Assignee">
                <CaseAssignee workCase={c} />
            </Fact>
        </div>
    );
}

function PriorityValue({ workCase: c }: { workCase: WorkQueueCase }) {
    return (
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <PriorityGlyph priority={c.priority} />
            <span className={`text-[12.5px] font-semibold ${PRIORITY_LABEL_TONES[c.priority]}`}>
                {PRIORITY_LEVELS[c.priority].label}
            </span>
        </span>
    );
}

function BandRow({ label, chips }: { label: string; chips: readonly string[] }) {
    if (chips.length === 0) return null;
    return (
        <div className="grid grid-cols-[84px_1fr] items-start gap-2">
            <span className={`${SECTION_LABEL} pt-[3px]`}>{label}</span>
            <div className="flex flex-wrap gap-1">
                {chips.map((chip) => (
                    <span key={chip} className={BAND_CHIP}>
                        {chip}
                    </span>
                ))}
            </div>
        </div>
    );
}

function UpNextCard({ link }: { link: NextCaseLink }) {
    const c = link.workCase;
    const practiceLabel = practiceDoctorLabel(c);

    return (
        <div className="w-[460px] max-w-full overflow-hidden rounded-[11px]">
            <div className="px-4 pt-3.5 pb-3">
                <div className="flex items-baseline justify-between gap-3">
                    <span className={SECTION_LABEL}>Up next</span>
                    <span className="text-[11px] text-gray-500">
                        {link.remaining.toLocaleString()} left in this list
                    </span>
                </div>
                <div className="mt-2.5 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center font-bold text-[13px] select-none ring-2 ring-white shadow-sm bg-yellow-400 text-[#4A3A02]">
                        {initials(c.name)}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[14px] font-bold text-gray-900 leading-snug">
                            {c.name}
                        </div>
                        {practiceLabel && (
                            <div className="text-[12px] text-gray-500 leading-snug line-clamp-2">
                                {practiceLabel}
                            </div>
                        )}
                    </div>
                    <div className="flex-shrink-0">
                        <PriorityValue workCase={c} />
                    </div>
                </div>
            </div>
            <CaseFacts workCase={c} />
            {hasListBand(link) && (
                <div className="flex flex-col gap-1.5 border-t border-gray-100 bg-gray-50 px-4 py-2.5">
                    <BandRow label="Filtered by" chips={link.filterChips} />
                    <BandRow label="Sorted by" chips={link.sortChip ? [link.sortChip] : []} />
                </div>
            )}
        </div>
    );
}

function hasListBand(link: NextCaseLink): boolean {
    return link.filterChips.length > 0 || link.sortChip !== null;
}

// The arrow sits on the card's bottom edge, so it takes the band's grey when the band is there.
const ARROW_ON_BAND = '[&>span>svg]:fill-gray-50';

export function NextCaseButton({ link }: { link: NextCaseLink }) {
    return (
        <Tooltip
            content={<UpNextCard link={link} />}
            side="top"
            align="start"
            surface="card"
            className={`max-w-[460px] ${hasListBand(link) ? ARROW_ON_BAND : ''}`}
        >
            <button
                type="button"
                onClick={link.onOpen}
                className="flex items-center gap-1.5 px-4 py-2 rounded-[8px] border border-gray-200 bg-white text-[13px] font-semibold text-[#565D67] hover:bg-gray-50 transition-colors"
            >
                Next case
                <span className="font-medium text-gray-400">
                    · {link.remaining.toLocaleString()} left
                </span>
                <ChevronRightIcon aria-hidden="true" className="w-3.5 h-3.5" />
            </button>
        </Tooltip>
    );
}
